// AnnouncementChatEvent — green-tinted card that renders inline among
// team chat messages whenever a competition-scoped announcement was
// published. Truncates the body to ~3 lines and ends with a "See more"
// link that opens the program-scoped announcements overlay.

import { Platform, Pressable, Text, View } from "react-native";

import { COLORS, FONTS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;
import Svg, { Path } from "react-native-svg";

import type { MyAnnouncement } from "../../lib/announcements";


const BODY_PREVIEW_CHAR_CAP = 180;

export type AnnouncementChatEventProps = {
  announcement: MyAnnouncement;
  /** Display name of the person who posted (resolved by parent from
   *  `announcement.created_by`). Falls back to "Coach" if null. */
  authorName: string | null;
  onPressSeeMore: () => void;
  /** Captains see a small ✕ in the top-right that calls onDelete. */
  canDelete?: boolean;
  onDelete?: (announcement: MyAnnouncement) => void;
};

export function AnnouncementChatEvent({
  announcement,
  authorName,
  onPressSeeMore,
  canDelete,
  onDelete,
}: AnnouncementChatEventProps) {
  const body = announcement.body ?? "";
  const truncated = body.length > BODY_PREVIEW_CHAR_CAP;
  const preview = truncated
    ? body.slice(0, BODY_PREVIEW_CHAR_CAP).trimEnd() + "…"
    : body;

  const overline = (() => {
    const who = authorName ?? "Coach";
    const at = formatAnnouncedAt(announcement.published_at ?? announcement.created_at);
    return who + " · " + at;
  })();

  return (
    <View
      style={{
        marginVertical: 6,
        backgroundColor: STUDENT_THEME.successTintSoft,
        borderWidth: 1,
        borderColor: "rgba(100,127,77,0.55)",
        borderRadius: 8,
        padding: 12,
      }}
    >
      {/* Top row: announcement icon + label on the left, DELETE pill on
          the right. Pulled out of absolute positioning because RN-Web
          drops pointer events on absolute children when there's a sibling
          stretching the full card width. Inline flex layout means every
          pixel of the pill (and its hit area) is hot. */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 6,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8, flexShrink: 1, minWidth: 0 }}>
          <Svg width={14} height={14} viewBox="0 0 20 20" fill="none">
            <Path
              d="M3 8 L3 12 L6 12 L13 16 L13 4 L6 8 Z"
              fill={COLORS.SAGE_DEEP.hex}
              stroke={COLORS.SAGE_DEEP.hex}
              strokeWidth={0.5}
              strokeLinejoin="round"
            />
            <Path
              d="M15 7 Q17 10 15 13"
              stroke={COLORS.SAGE_DEEP.hex}
              strokeWidth={1.4}
              fill="none"
              strokeLinecap="round"
            />
          </Svg>
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9,
              letterSpacing: 1.6,
              color: "#3F5A30",
            }}
            numberOfLines={1}
          >
            ANNOUNCEMENT · {overline.toUpperCase()}
          </Text>
        </View>
        {canDelete && onDelete ? (
          <Pressable
            onPress={() => onDelete(announcement)}
            accessibilityRole="button"
            accessibilityLabel="Delete announcement"
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={({ hovered }: { hovered?: boolean }) => [
              {
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                paddingHorizontal: 12,
                paddingVertical: 8,
                borderRadius: 5,
                borderWidth: 1,
                borderColor:
                  hovered && Platform.OS === "web"
                    ? "#3F5A30"
                    : STUDENT_THEME.successBorderSoft,
                backgroundColor:
                  hovered && Platform.OS === "web"
                    ? "rgba(100,127,77,0.18)"
                    : "rgba(100,127,77,0.06)",
                flexShrink: 0,
              },
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Svg width={13} height={13} viewBox="0 0 16 16" fill="none">
              <Path
                d="M2.5 4 H13.5"
                stroke={"#3F5A30"}
                strokeWidth={1.5}
                strokeLinecap="round"
              />
              <Path
                d="M6 4 V2.5 A0.5 0.5 0 0 1 6.5 2 H9.5 A0.5 0.5 0 0 1 10 2.5 V4"
                stroke={"#3F5A30"}
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
              <Path
                d="M4.5 4 L5.25 13.25 A1 1 0 0 0 6.25 14.25 H9.75 A1 1 0 0 0 10.75 13.25 L11.5 4"
                stroke={"#3F5A30"}
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
              <Path
                d="M7 7 V12 M9 7 V12"
                stroke={"#3F5A30"}
                strokeWidth={1.4}
                strokeLinecap="round"
              />
            </Svg>
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 10.5,
                letterSpacing: 1.3,
                color: "#3F5A30",
              }}
            >
              DELETE
            </Text>
          </Pressable>
        ) : null}
      </View>
      <Text
        style={{
          fontFamily: FONTS.displayMedium,
          fontSize: 16,
          color: COLORS.FOREST.hex,
          lineHeight: 21,
          marginBottom: body.length > 0 ? 4 : 0,
        }}
        numberOfLines={2}
      >
        {announcement.title}
      </Text>
      {body.length > 0 ? (
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 13,
            color: "rgba(28,43,26,0.85)",
            lineHeight: 19,
          }}
        >
          {preview}
          {truncated ? (
            <Text>
              {"  "}
              <Text
                onPress={onPressSeeMore}
                style={[
                  {
                    fontFamily: FONTS.bodyBold,
                    color: "#3F5A30",
                  },
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                See more →
              </Text>
            </Text>
          ) : null}
        </Text>
      ) : null}
      {!body.length || !truncated ? (
        <Pressable
          onPress={onPressSeeMore}
          accessibilityRole="button"
          accessibilityLabel="Open announcement"
          style={[
            {
              alignSelf: "flex-start",
              marginTop: 8,
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
            },
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10.5,
              letterSpacing: 1.2,
              color: "#3F5A30",
            }}
          >
            OPEN
          </Text>
          <Text style={{ fontSize: 11, color: "#3F5A30" }}>→</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

function formatAnnouncedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const _UNUSED = { TEXT: COLORS.FOREST.hex, TEXT_MUTED: STUDENT_THEME.textOnSurfaceDim };
void _UNUSED;
